/**
 * Centralized access to Vite-injected environment variables.
 *
 * Falls back to QLASH staging defaults so the dev server runs out of the box
 * without requiring a manual .env.local. Override any value by copying
 * .env.example to .env.local and editing.
 */

const orDefault = (value: string | undefined, fallback: string): string =>
  value && value.length > 0 ? value : fallback;

// In dev mode RTK Query talks to the Vite proxy (`/qlash-api`, `/qnews-api`)
// which forwards to the real backend — bypasses CORS since the request is
// same-origin from the browser's perspective. In prod the bundle uses the
// absolute URL directly (the deployed origin must be CORS-whitelisted on the
// Laravel backend).
const isDev = import.meta.env.DEV;

export const env = {
  apiUrl: isDev
    ? '/qlash-api'
    : orDefault(
        import.meta.env.VITE_API_URL as string | undefined,
        'https://api.stg.tournaments.qlash.gg',
      ),
  newsApiUrl: isDev
    ? '/qnews-api'
    : orDefault(
        import.meta.env.VITE_NEWS_API_URL as string | undefined,
        'https://qnews.qlash.gg',
      ),
  avatarApiUrl: isDev
    ? '/qavatar-api'
    : orDefault(
        import.meta.env.VITE_AVATAR_API_URL as string | undefined,
        'https://qavatar.qlash.gg',
      ),
  qriotsApiUrl: isDev
    ? '/qriots-api'
    : orDefault(
        import.meta.env.VITE_QRIOTS_API_URL as string | undefined,
        'https://qriots.qlash.gg',
      ),
  // QBot — Clash Royale data + AI coaching. Same Logto API resource as the
  // backoffice (https://qbot.qlash.gg) so no new Logto registration needed.
  qbotApiUrl: isDev
    ? '/qbot-api'
    : orDefault(
        import.meta.env.VITE_QBOT_API_URL as string | undefined,
        'https://qbot.qlash.gg',
      ),
  // Customization microservice (banners, IAP, purchases). Mobile-app calls
  // `https://v2-api.qlash.gg/customization` directly — we proxy through Vite
  // in dev to bypass CORS, same pattern as the other backends.
  customizationApiUrl: isDev
    ? '/qlash-customization'
    : orDefault(
        import.meta.env.VITE_CUSTOMIZATION_API_URL as string | undefined,
        'https://v2-api.qlash.gg/customization',
      ),
  // External web shop URL — opened in system browser via openLink when the
  // user taps "Get more banners". Real banner purchases happen in the mobile
  // app (native IAP); the Mini App stays read-only for Phase 1.
  shopUrl: orDefault(
    import.meta.env.VITE_SHOP_URL as string | undefined,
    'https://qlash.gg/shop',
  ),
  wsUrl: orDefault(
    import.meta.env.VITE_WS_URL as string | undefined,
    'wss://ws.tournaments.qlash.gg/connection/websocket',
  ),
  logtoEndpoint: orDefault(
    import.meta.env.VITE_LOGTO_ENDPOINT as string | undefined,
    'https://stg-account.qlash.gg/',
  ),
  logtoAppId: orDefault(
    import.meta.env.VITE_LOGTO_APP_ID as string | undefined,
    'qdaeddjw8d0jdw4r884ml',
  ),
  logtoResource: orDefault(
    import.meta.env.VITE_LOGTO_RESOURCE as string | undefined,
    'https://api.tournaments.qlash.gg',
  ),
  telegramBotUsername: orDefault(
    import.meta.env.VITE_TG_BOT_USERNAME as string | undefined,
    'QLASH_APP_bot',
  ),
  platformId: orDefault(
    import.meta.env.VITE_PLATFORM_ID as string | undefined,
    'qlash',
  ),
  enableEruda: import.meta.env.VITE_ENABLE_ERUDA === 'true',
  isDev: import.meta.env.DEV,
} as const;
